import React, {
  ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
// Translation Package
import { useTranslation } from "react-i18next";

import countries from "Components/Form/Helpers/JSON/Countries.json";

// Constants
import * as ROUTES from "../../../Constants/Routes";
import {
  DATE_OF_BIRTH_FORMAT,
  EMAIL_PATTERN,
  ERROR_MESSAGES,
  NAME_PATTERN,
  NEW_PASSWORD_PROPERTIES,
} from "../constants";

// Packages
import dayjs from "dayjs";
import firebase from "firebase/compat/app";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useHistory, useLocation } from "react-router-dom";

import redirectAfterLoginSuccess from "Components/Auth/functions/redirectAfterLoginSuccess";
import logGaEvent from "../../../lib/ga/logGaEvent";

// Components
import FormInput from "../../Form/FormInput";
import Loading from "../../Utils/Loading";
import PasswordInfo from "../Helpers/PasswordInfo";

// Providers
import {
  DEFAULT_STATE as DEFAULT_MODAL,
  useModal,
  useUpdateModal,
} from "../../../Providers/FloatingModalProvider";

// Functions
import { contactReviewFormState } from "Components/Booking/Payment/Forms/ContactReview/formState";
import PhoneNumberInput from "Components/Form/PhoneNumberInput";
import { URL_PARAMS } from "Constants/globals";
import {
  ContactSignUpPayloadInput,
  useSignInContactMutation,
  useSignUpContactWithEmailPasswordMutation,
} from "lib/apollo-v2/generated/graphql";
import { useUser } from "Providers/UserProvider";
import PrimaryButton from "../../Buttons/Primary";
import trimWhiteSpace from "../../Utils/validate";
import validatePhone from "../../Utils/validatePhone";
import checkNewPassword from "../functions/checkNewPassword";
import formatDateOfBirth from "../functions/formatDateOfBirth";
import validateSignupForm from "../functions/validateSignupForm";
import { triggerPaymentMethodSelectionVar } from "./trigger";

export interface SignUpWithEmailFormHandle {
  signup: () => Promise<void>;
}

export interface SignUpFormValues {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  country: string;
  password: string;
  passwordConfirm: string;
  dateOfBirth?: string;
}

interface SignUpWithEmailFormProps {
  redirectTo?: string;
  urlParams?: string;
  isRoute?: boolean;
  routeStateEmail?: string;
  isInlineSignup?: boolean;
  setIsSigningUp?: (value: boolean) => void;
  isSubscribedToNewsLetter?: boolean;
  defaultFormValues?: Partial<SignUpFormValues>;
  /** Optional callback invoked after successful signup instead of page reload/redirect */
  onSuccess?: () => void;
}

const SignUpWithEmailForm = forwardRef<
  SignUpWithEmailFormHandle,
  SignUpWithEmailFormProps
>(function SignupWithEmailForm(formProps, ref) {
  const {
    redirectTo,
    urlParams,
    isRoute,
    routeStateEmail,
    isInlineSignup = false,
    setIsSigningUp = (value: boolean) => {},
    isSubscribedToNewsLetter,
    defaultFormValues = {},
  } = formProps;

  // Translation hook
  const { t } = useTranslation();

  // Location data
  const { pathname, search } = useLocation();
  const getSearchParams = new URLSearchParams(search)?.toString();
  let setSearchParams = null;
  if (getSearchParams) {
    setSearchParams = `&${new URLSearchParams(search)?.toString()}`;
  }

  // Handle setting user
  const { setUser, refreshUser } = useUser();

  const history = useHistory();

  const { props } = useModal();
  const updateModal = useUpdateModal();

  /** onSuccess callback propagated from the auth gate flow (via modal props or direct prop) */
  const onSuccess =
    formProps.onSuccess ?? (props?.onSuccess as (() => void) | undefined);

  // Handle date of birth masking
  const [dateOfBirth, setDateOfBirth] = useState("");
  const handleDateOfBirth = (e: ChangeEvent<HTMLInputElement>) => {
    formatDateOfBirth(e.target.value, setDateOfBirth);
  };
  const handleOnDateOfBirthKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const keys = {
      backspace: 8,
    };
    let val = (e.target as HTMLInputElement).value;

    // Detect remove
    if (e.which === keys.backspace) {
      if (val.length === 3 || val.length === 6) {
        val = val.slice(0, val.length - 1);
        setDateOfBirth(val);
      }
    }
  };

  // Handle password
  const [password, setPassword] = useState(NEW_PASSWORD_PROPERTIES);
  const handlePasswordOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const pass = e.target.value;
    const validations = checkNewPassword(pass);

    setPassword((p) => ({
      ...p,
      ...validations,
      value: pass,
    }));
  };
  const handlePasswordOnFocus = () => {
    setPassword((p) => ({ ...p, focused: true }));
  };

  const [disabledButton, setDisabledButton] = useState(false);

  // Form handling
  const [mutationError, setMutationError] = useState<string | null>(null);
  const formRef = useRef<HTMLButtonElement>(null);

  // Using un-typed useForm then casting register/etc is easier to avoid overload hell
  // but let's try to stick to useForm<SignUpFormValues> and cast specific validation fns if needed
  const methods = useForm({
    mode: "onBlur",
    reValidateMode: "onSubmit",
    defaultValues: {
      email: isRoute ? routeStateEmail : props?.email,
      ...defaultFormValues,
    },
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    trigger,
    getValues,
  } = methods;

  const [addNewUser] = useSignUpContactWithEmailPasswordMutation({
    // client: clientV2,
  });
  const [login] = useSignInContactMutation({
    // client: clientV2,
  });

  const resetDisabledButton = () => {
    // UX feature
    // Add additional waiting to indicate the form is working smoothly
    setTimeout(() => {
      setDisabledButton(false);
      setIsSigningUp(false);
    }, 700);
    return;
  };

  const onSubmit = async (data: SignUpFormValues) => {
    setDisabledButton(true);
    setIsSigningUp(true);
    clearErrors();
    setMutationError(null);

    // Validations and set custom errors
    const validations = validateSignupForm(data, !isInlineSignup);
    const doesAllPass = validations.doesAllPass;
    if (doesAllPass === false) {
      if (!validations.isDateOfBirthValidAge) {
        setError("dateOfBirth", {
          type: "manual",
          message: t(ERROR_MESSAGES.dateOfBirth.underAge),
        });
      }
      if (!validations.doesDateExist) {
        setError("dateOfBirth", {
          type: "manual",
          message: t(ERROR_MESSAGES.dateOfBirth.invalidFormat),
        });
      }
      resetDisabledButton();
      return;
    }

    // Password validation
    if (password.isValid === false) {
      setError("password", { type: "manual", message: "" });
      resetDisabledButton();
      return;
    }

    // Password confirm validation
    if (data.password !== data.passwordConfirm) {
      setError("passwordConfirm", {
        type: "manual",
        message: t(ERROR_MESSAGES.passwordConfirm.doesNotMatch),
      });
      resetDisabledButton();
      return;
    }

    const mobile = data.mobile.replaceAll(" ", "");
    const countryCode = countries.find(
      (c) => c.code === data.country,
    )?.dial_code;

    let payload: ContactSignUpPayloadInput = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: {
        number: mobile,
        code: "+" + countryCode,
      },
      password: data.password,
    };

    if (!isInlineSignup) {
      const dob = dayjs(data.dateOfBirth, DATE_OF_BIRTH_FORMAT.toUpperCase());
      payload = {
        ...payload,
        birthdate: dob.format("YYYY-MM-DD"),
      };
    } else {
      payload = {
        ...payload,
        isSubscribedToNewsLetter,
      };
    }

    await addNewUser({ variables: { payload } })
      .then((response) => {
        const jwtToken = response.data.result.jwtToken;
        localStorage.setItem("token", jwtToken);
      })
      .then(async () => {
        // Login and redirect
        await firebase
          .auth()
          .signInWithEmailAndPassword(data.email, data.password)
          .then(async (res) => await res.user.getIdToken())
          .then(async (token) => {
            return await login({
              variables: { firebaseToken: token, payload: {} },
              update: () => {
                setUser({ ...payload, birthdate: payload.birthdate ?? "" });
              },
            });
          })
          .then((res) => {
            localStorage.setItem("token", res.data.result.jwtToken);
            if (isInlineSignup) {
              contactReviewFormState({
                firstName: payload.firstName,
                lastName: payload.lastName,
                email: payload.email,
                mobile: payload.phone?.number ?? "",
              });
              triggerPaymentMethodSelectionVar(payload.email);
            }
          })
          .then(() => {
            logGaEvent("sign_up", "email");

            setTimeout(async () => {
              if (onSuccess) {
                updateModal(DEFAULT_MODAL);
                await refreshUser();
                onSuccess();
              } else if (redirectTo) {
                redirectAfterLoginSuccess(redirectTo, urlParams);
              } else if (!isInlineSignup) {
                history.go(0);
              }
            }, 300);
          })
          .catch(() => {
            setMutationError(t("error_general"));
            setDisabledButton(false);
            setIsSigningUp(false);
          });

        updateModal(DEFAULT_MODAL);
        return;
      })
      .catch((error) => {
        error = { ...error };
        setMutationError(error.message ?? t("error_general"));
        setDisabledButton(false);
        setIsSigningUp(false);
        window.scrollTo({ top: 0, left: 0 });
        return error;
      })
      .finally(() => {
        // Only enable button if we're not successful (though redirects usually happen before this matters much?)
        // But keeping logic consistent with existing components
        if (mutationError) {
          setDisabledButton(false);
          setIsSigningUp(false);
        }
      });
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        async signup() {
          //   formRef.current?.dispatchEvent(new Event("submit"));
          formRef?.current?.click();
        },
      };
    },
    [],
  );

  return (
    <FormProvider {...methods}>
      <div>
        {/* Mixed Standard / Inline */}
        {!isInlineSignup ? (
          <div className="auth-form">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="auth-form__signup-with-email"
            >
              <div className="row">
                <div className="col-12 ">
                  <h6 className="auth-form__form-title">
                    {t("auth_create_account")}
                  </h6>
                </div>
              </div>

              {mutationError && (
                <div className="row">
                  <div className="col-12 auth-form__error">{mutationError}</div>
                </div>
              )}
              {/* Form */}

              <div className="row d-flex flex-column gap-3">
                {/* EMAIL */}
                {/* Email address */}
                <div className="col-12 ">
                  <FormInput
                    register={register("email", {
                      required: {
                        value: true,
                        message: t(ERROR_MESSAGES.email.required),
                      },
                      pattern: {
                        value: EMAIL_PATTERN,
                        message: t(ERROR_MESSAGES.email.invalidFormat),
                      },
                      validate: trimWhiteSpace() as any,
                    })}
                    type="email"
                    name="email"
                    label={t("email")}
                    autoFocus={routeStateEmail || props?.email ? false : true}
                    error={errors.email}
                    data-test-id="email-input"
                  />
                </div>

                {/* Mobile */}
                <div className="col-12">
                  <PhoneNumberInput
                    defaultNumber={defaultFormValues?.mobile}
                    phoneNumberRegister={register("mobile", {
                      required: {
                        value: true,
                        message: t(ERROR_MESSAGES.mobile.required),
                      },
                      minLength: {
                        value: 5,
                        message: t(ERROR_MESSAGES.mobile.invalidFormat),
                      },
                      validate: (value, values) =>
                        validatePhone(value, values["country"]),
                    })}
                    autoFocus={routeStateEmail || props?.email ? true : false}
                    phoneNumberError={errors.mobile}
                    // countryError={errors.country}
                    // getCountry={() => getValues("country")}
                    // countryRegister={register("country", {
                    //   required: {
                    //     value: true,
                    //     message: t(ERROR_MESSAGES.countryCode.required),
                    //   },
                    //   validate: trimWhiteSpace() as any,
                    // })}
                    // triggerCountryValidation={() => trigger("country")}
                  />
                </div>

                {/* First name */}
                <div className="col-12">
                  <FormInput
                    register={register("firstName", {
                      required: {
                        value: true,
                        message: t(ERROR_MESSAGES.firstName.required),
                      },
                      validate: trimWhiteSpace() as any,
                      pattern: {
                        value: NAME_PATTERN,
                        message: t("error_name_characters"),
                      },
                    })}
                    type="text"
                    name="firstName"
                    label={t("first_name")}
                    error={errors.firstName}
                    data-test-id="first-name"
                  />
                </div>
                {/* Last name */}
                <div className="col-12">
                  <FormInput
                    register={register("lastName", {
                      required: {
                        value: true,
                        message: t(ERROR_MESSAGES.lastName.required),
                      },
                      validate: trimWhiteSpace() as any,
                      pattern: {
                        value: NAME_PATTERN,
                        message: t("error_name_characters"),
                      },
                    })}
                    type="text"
                    name="lastName"
                    label={t("last_name")}
                    error={errors.lastName}
                    info={t("name_id_notice")}
                    data-test-id="last-name"
                  />
                </div>

                {/* Birthdate */}
                <div className="col-12">
                  <div className="row">
                    <div className="col-12">
                      <FormInput
                        register={register("dateOfBirth", {
                          required: {
                            value: true,
                            message: t(ERROR_MESSAGES.dateOfBirth.required),
                          },
                          minLength: {
                            value: 10,
                            message: t(
                              ERROR_MESSAGES.dateOfBirth.invalidFormat,
                            ),
                          },
                        })}
                        inputMode="numeric"
                        type="text"
                        name="dateOfBirth"
                        label={t("date_of_birth")}
                        value={dateOfBirth}
                        onChange={handleDateOfBirth}
                        onKeyDown={handleOnDateOfBirthKeyDown}
                        forceLabelToFloat={true}
                        error={errors.dateOfBirth}
                        placeholder={DATE_OF_BIRTH_FORMAT}
                        data-test-id="date-of-birth"
                      />
                    </div>
                  </div>
                </div>

                {/* Password */}
                <div className="col-12">
                  <FormInput
                    register={register("password", {
                      required: {
                        value: true,
                        message: t(ERROR_MESSAGES.password.required),
                      },
                    })}
                    type="password"
                    name="password"
                    label={t("password")}
                    value={password.value}
                    onFocus={handlePasswordOnFocus}
                    onChange={handlePasswordOnChange}
                    autoComplete="new-password"
                    spellCheck="false"
                    error={errors.password}
                    data-test-id="password"
                  />

                  <PasswordInfo password={password} />
                </div>

                {/* Password confirm*/}
                <div className="col-12">
                  <FormInput
                    register={register("passwordConfirm", {
                      required: {
                        value: true,
                        message: t(ERROR_MESSAGES.passwordConfirm.required),
                      },
                    })}
                    type="password"
                    name="passwordConfirm"
                    label={t("confirm_password")}
                    autoComplete="new-password"
                    spellCheck="false"
                    error={errors.passwordConfirm}
                    data-test-id="confirm-password"
                  />
                </div>

                {/* Agreement */}
                <div className="col-12 mt-3">
                  <p className="info">
                    {t("signup_agreement")}&apos;{" "}
                    <Link to={ROUTES.TERMS_AND_CONDITIONS} target="_blank">
                      {t("terms_and_conditions")}
                    </Link>{" "}
                    {t("and")}{" "}
                    <Link to={ROUTES.PRIVACY_POLICY} target="_blank">
                      {t("privacy_policy")}
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <div
                    className="form-button mt-1"
                    style={{ display: "block" }}
                  >
                    <PrimaryButton
                      label={
                        disabledButton ? (
                          <Loading light />
                        ) : (
                          t("agree_continue_button")
                        )
                      }
                      disabled={disabledButton}
                      data-test-id="agree-continue-button"
                    />
                  </div>
                </div>
              </div>
              {/* Hidden button for imperative submisson if ever needed, though Standard uses PrimaryButton */}
              <button type="submit" ref={formRef} className="d-none"></button>
            </form>
          </div>
        ) : (
          <div style={{ marginBottom: "-20px" }}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="auth-form__signup-with-email mb-3"
              // ref={formRef}
            >
              <div className="confirm-and-pay__form">
                {/* Title */}
                <div className="form-title mt-0">
                  <h5 className="form-title__title">
                    {t("inline_signup_personal_information")}
                  </h5>
                </div>
                <div className="col-12 info" style={{ fontSize: "small" }}>
                  <span>{`${t("sign_in_question")} `}</span>
                  <Link
                    to={`${ROUTES.LOGIN}?${URL_PARAMS.REDIRECT}=${pathname}${
                      setSearchParams ?? ""
                    }`}
                  >
                    {t("login")}
                  </Link>
                </div>
                <div className="row mt-2">
                  {/* First name */}
                  <div className="col-12 col-md-6">
                    <FormInput
                      register={register("firstName", {
                        required: {
                          value: true,
                          message: t(ERROR_MESSAGES.firstName.required),
                        },
                        validate: trimWhiteSpace() as any,
                        pattern: {
                          value: NAME_PATTERN,
                          message: t("error_name_characters"),
                        },
                      })}
                      type="text"
                      name="firstName"
                      label={t("first_name")}
                      error={errors.firstName}
                      data-test-id="first-name"
                    />
                  </div>
                  {/* Last name */}
                  <div className="col-12 col-md-6">
                    <FormInput
                      register={register("lastName", {
                        required: {
                          value: true,
                          message: t(ERROR_MESSAGES.lastName.required),
                        },
                        validate: trimWhiteSpace() as any,
                        pattern: {
                          value: NAME_PATTERN,
                          message: t("error_name_characters"),
                        },
                      })}
                      type="text"
                      name="lastName"
                      label={t("last_name")}
                      error={errors.lastName}
                      data-test-id="last-name"
                    />
                  </div>
                </div>
                <div className="col-12">
                  <p className="info mb-3">{t("name_id_notice")}</p>
                </div>
                <div className="row">
                  <div className="col-12">
                    <PhoneNumberInput
                      phoneNumberRegister={register("mobile", {
                        required: {
                          value: true,
                          message: t(ERROR_MESSAGES.mobile.required),
                        },
                        minLength: {
                          value: 5,
                          message: t(ERROR_MESSAGES.mobile.invalidFormat),
                        },
                        validate: (value, values) =>
                          validatePhone(value, values["country"]),
                      })}
                      // countryRegister={register("country", {
                      //   required: true,
                      //   validate: trimWhiteSpace() as any,
                      // })}
                      // getCountry={() => getValues("country")}
                      // triggerCountryValidation={() => trigger("country")}
                      autoFocus={routeStateEmail || props?.email ? true : false}
                      phoneNumberError={errors.mobile}
                      // countryError={errors.country}
                      data-test-id="mobile-input"
                      countryColSpan={"col-6"}
                      phoneNumberColSpan={"col-6"}
                      defaultNumber={defaultFormValues?.mobile}
                    />
                  </div>
                </div>
                <div className="row">
                  {/* Email address */}
                  <div className="col-12 col-md-6">
                    <FormInput
                      register={register("email", {
                        required: {
                          value: true,
                          message: t(ERROR_MESSAGES.email.required),
                        },
                        pattern: {
                          value: EMAIL_PATTERN,
                          message: t(ERROR_MESSAGES.email.invalidFormat),
                        },
                        validate: trimWhiteSpace() as any,
                      })}
                      type="email"
                      name="email"
                      label={t("email")}
                      autoFocus={routeStateEmail || props?.email ? false : true}
                      error={errors.email}
                      data-test-id="email-input"
                    />
                  </div>
                </div>
                <div className="col-12">
                  <p className="info mb-3">
                    {t("inline_signup_email_field_notice")}
                  </p>
                </div>
                <div className="row">
                  {/* Password */}
                  <div className="col-12 col-md-6">
                    <FormInput
                      register={register("password", {
                        required: {
                          value: true,
                          message: t(ERROR_MESSAGES.password.required),
                        },
                      })}
                      type="password"
                      name="password"
                      label={t("password")}
                      value={password.value}
                      onFocus={handlePasswordOnFocus}
                      onChange={handlePasswordOnChange}
                      autoComplete="new-password"
                      spellCheck="false"
                      error={errors.password}
                      data-test-id="password"
                    />
                    <PasswordInfo password={password} />
                  </div>

                  {/* Password confirm*/}
                  <div className="col-12 col-md-6">
                    <FormInput
                      register={register("passwordConfirm", {
                        required: {
                          value: true,
                          message: t(ERROR_MESSAGES.passwordConfirm.required),
                        },
                      })}
                      type="password"
                      name="passwordConfirm"
                      label={t("confirm_password")}
                      autoComplete="new-password"
                      spellCheck="false"
                      error={errors.passwordConfirm}
                      data-test-id="confirm-password"
                    />
                  </div>
                </div>
                {mutationError && (
                  <div className="row">
                    <div className="col-12 auth-form__error fs-6">
                      {mutationError}
                    </div>
                  </div>
                )}
              </div>
              <button type="submit" ref={formRef} className="d-none"></button>
            </form>
          </div>
        )}
      </div>
    </FormProvider>
  );
});

export default SignUpWithEmailForm;
